import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const VacationRequestModal = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  existingRequests 
}) => {
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    type: 'vacation',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const vacationTypes = [
    { value: 'vacation', label: 'Urlaub', icon: 'Plane' },
    { value: 'sick_leave', label: 'Krankenstand', icon: 'Heart' },
    { value: 'personal', label: 'Persönlicher Grund', icon: 'User' },
    { value: 'training', label: 'Schulung/Weiterbildung', icon: 'GraduationCap' },
    { value: 'other', label: 'Sonstiges', icon: 'MoreHorizontal' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.startDate) {
      newErrors.startDate = 'Startdatum ist erforderlich';
    }
    
    if (!formData?.endDate) {
      newErrors.endDate = 'Enddatum ist erforderlich';
    }
    
    if (formData?.startDate && formData?.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      
      if (start > end) {
        newErrors.endDate = 'Enddatum muss nach dem Startdatum liegen';
      }
      
      if (start < new Date()) {
        newErrors.startDate = 'Startdatum kann nicht in der Vergangenheit liegen';
      }
    }
    
    if (!formData?.reason?.trim()) {
      newErrors.reason = 'Grund ist erforderlich';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
      setFormData({
        startDate: '',
        endDate: '',
        reason: '',
        type: 'vacation',
        notes: ''
      });
      setErrors({});
      onClose();
    }
  };

  const calculateDays = () => {
    if (formData?.startDate && formData?.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    return 0;
  };

  const hasConflict = () => {
    if (!formData?.startDate || !formData?.endDate) return false;
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    
    return existingRequests?.some(request => {
      const reqStart = new Date(request.startDate);
      const reqEnd = new Date(request.endDate);
      
      return (start <= reqEnd && end >= reqStart);
    });
  };

  const getSelectedTypeIcon = () => {
    const selectedType = vacationTypes?.find(type => type?.value === formData?.type);
    return selectedType ? selectedType?.icon : 'Calendar';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg border border-border shadow-modal max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="Calendar" size={24} className="text-primary" />
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Abwesenheitsantrag stellen
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Beantragen Sie Urlaub oder andere Abwesenheiten
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Vacation Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Art der Abwesenheit
            </label>
            <div className="grid grid-cols-2 gap-2">
              {vacationTypes?.map(type => (
                <button
                  key={type?.value}
                  type="button"
                  onClick={() => handleInputChange('type', type?.value)}
                  className={`
                    p-3 rounded-lg border transition-all duration-200 text-left
                    ${formData?.type === type?.value 
                      ? 'border-primary bg-primary bg-opacity-10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary hover:text-foreground'
                    }
                  `}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name={type?.icon} size={16} />
                    <span className="text-sm font-medium">{type?.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Startdatum"
              type="date"
              value={formData?.startDate}
              onChange={(e) => handleInputChange('startDate', e?.target?.value)}
              error={errors?.startDate}
              required
            />
            <Input
              label="Enddatum"
              type="date"
              value={formData?.endDate}
              onChange={(e) => handleInputChange('endDate', e?.target?.value)}
              error={errors?.endDate}
              required
            />
          </div>

          {/* Duration Info */}
          {calculateDays() > 0 && (
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Dauer:</span>
                <span className="font-medium text-foreground">
                  {calculateDays()} Tag{calculateDays() !== 1 ? 'e' : ''}
                </span>
              </div>
              {hasConflict() && (
                <div className="flex items-center space-x-2 mt-2 text-warning">
                  <Icon name="AlertTriangle" size={14} />
                  <span className="text-xs">Überschneidung mit bestehenden Anträgen</span>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <Input
            label="Grund"
            type="text"
            placeholder="Kurze Beschreibung des Grundes"
            value={formData?.reason}
            onChange={(e) => handleInputChange('reason', e?.target?.value)}
            error={errors?.reason}
            required
          />

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Zusätzliche Notizen (optional)
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              placeholder="Weitere Informationen oder besondere Umstände..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Existing Requests Preview */}
          {existingRequests?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">
                Bestehende Anträge
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {existingRequests?.slice(0, 3)?.map((request, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                    <span className="text-foreground">
                      {new Date(request.startDate)?.toLocaleDateString('de-DE')} - {new Date(request.endDate)?.toLocaleDateString('de-DE')}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      request?.status === 'approved' ? 'bg-success text-success-foreground' :
                      request?.status === 'pending' ? 'bg-warning text-warning-foreground' :
                      'bg-error text-error-foreground'
                    }`}>
                      {request?.status === 'approved' ? 'Genehmigt' :
                       request?.status === 'pending' ? 'Ausstehend' : 'Abgelehnt'}
                    </span>
                  </div>
                ))}
                {existingRequests?.length > 3 && (
                  <p className="text-xs text-muted-foreground text-center">
                    +{existingRequests?.length - 3} weitere Anträge
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName={getSelectedTypeIcon()}
              iconPosition="left"
              iconSize={16}
              className="flex-1"
              disabled={hasConflict()}
            >
              Antrag stellen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VacationRequestModal;